import { apiFetch } from "../api";
import type {
    ObjectiveDraftCommitRequest,
    ObjectiveDraftCommitResponse,
    ObjectiveDraftCreateRequest,
    ObjectiveDraftRegenerateRequest,
    ObjectiveDraftResponse,
} from "./draftTypes";
import type { DailyExecutionPoint, MovementPortfolioRollup, ObjectiveMovementSummary } from "./types";
import { IOpportunitiesResponse, OpportunityImpactedClient } from "./types";
import { normalizeAdvisorDisplayName, normalizeOpportunityAccountId } from "./accountNormalize";

const impactedAdvisorExtras = (
    src: Record<string, unknown>
): Pick<OpportunityImpactedClient, "advisor_name" | "advisor_id" | "advisor_element_id"> => ({
    advisor_name: normalizeAdvisorDisplayName(src["advisor_name"] ?? src["advisorName"]),
    advisor_id: normalizeAdvisorDisplayName(src["advisor_id"] ?? src["advisorId"]),
    advisor_element_id: normalizeAdvisorDisplayName(src["advisor_element_id"] ?? src["advisorElementId"]),
})

const normalizeRollupRow = (row: any): MovementPortfolioRollup => {
    const pf = row?.portfolio;
    const accountRaw = pf?.account ?? row?.portfolio_account ?? row?.account;
    const accountStr = normalizeOpportunityAccountId(accountRaw);
    const net = Number(row?.total_amount_net);
    return {
        portfolio_account: accountStr || undefined,
        account: accountStr || undefined,
        total_amount_net: Number.isFinite(net) ? net : 0,
        investment_advisor: row?.investment_advisor ?? null,
        portfolio_name: pf?.name ? String(pf.name).trim() : undefined,
        movement_count:
            typeof row?.movement_count === "number" ? row.movement_count : undefined,
    };
};

const normalizeDailyAdvisor = (row: Record<string, unknown>): DailyExecutionPoint["investment_advisor"] => {
    const nested = row.investment_advisor ?? row.investmentAdvisor;
    if (nested && typeof nested === "object") {
        const a = nested as Record<string, unknown>;
        const name = a.name != null ? String(a.name).trim() : "";
        const id = a.id != null ? String(a.id).trim() : "";
        const element_id =
            a.element_id != null
                ? String(a.element_id).trim()
                : a.elementId != null
                  ? String(a.elementId).trim()
                  : "";
        if (!name && !id && !element_id) return null;
        return {
            name: name || null,
            id: id || null,
            element_id: element_id || null,
        };
    }
    const name = row.advisor_name ?? row.advisorName;
    const id = row.advisor_id ?? row.advisorId;
    const element_id = row.advisor_element_id ?? row.advisorElementId;
    const nameS = name != null ? String(name).trim() : "";
    const idS = id != null ? String(id).trim() : "";
    const eidS = element_id != null ? String(element_id).trim() : "";
    if (!nameS && !idS && !eidS) return null;
    return {
        name: nameS || null,
        id: idS || null,
        element_id: eidS || null,
    };
};

const normalizeDailyExecutionPoint = (row: unknown): DailyExecutionPoint | null => {
    if (!row || typeof row !== "object") return null;
    const o = row as Record<string, unknown>;
    const dateRaw = o.date ?? o.day;
    if (dateRaw == null) return null;
    const date = String(dateRaw).trim().slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
    const net = Number(o.amount_net ?? o.amountNet);
    const count = Number(o.movement_count ?? o.movementCount);
    const investment_advisor = normalizeDailyAdvisor(o);
    return {
        date,
        amount_net: Number.isFinite(net) ? net : 0,
        movement_count: Number.isFinite(count) ? count : 0,
        investment_advisor,
        advisor_id: investment_advisor?.id ?? null,
        advisor_element_id: investment_advisor?.element_id ?? null,
        advisor_name: investment_advisor?.name ?? null,
    };
};

const normalizeDailyExecutionSeries = (raw: unknown): DailyExecutionPoint[] => {
    if (!Array.isArray(raw)) return [];
    return raw
        .map(normalizeDailyExecutionPoint)
        .filter((p): p is DailyExecutionPoint => p != null);
};

/** API may send an array of rows or `{ portfolio_rollups, summary, warnings, daily_execution_series }`. */
const normalizeMovementPortfolioRollupsField = (raw: unknown): {
    rows: MovementPortfolioRollup[];
    summary?: ObjectiveMovementSummary;
    dailyExecutionSeries: DailyExecutionPoint[];
    rollupWarnings: string[];
} => {
    if (raw == null) {
        return { rows: [], dailyExecutionSeries: [], rollupWarnings: [] };
    }
    if (Array.isArray(raw)) {
        return { rows: raw.map(normalizeRollupRow), dailyExecutionSeries: [], rollupWarnings: [] };
    }
    if (typeof raw === "object") {
        const o = raw as Record<string, unknown>;
        const inner = o.portfolio_rollups ?? o.portfolioRollups;
        if (Array.isArray(inner)) {
            const rw = o.warnings;
            const rollupWarnings = Array.isArray(rw)
                ? rw.filter((w): w is string => typeof w === "string")
                : [];
            const summary =
                o.summary && typeof o.summary === "object"
                    ? (o.summary as ObjectiveMovementSummary)
                    : undefined;
            const dailyExecutionSeries = normalizeDailyExecutionSeries(
                o.daily_execution_series ?? o.dailyExecutionSeries,
            );
            return {
                rows: inner.map(normalizeRollupRow),
                summary,
                dailyExecutionSeries,
                rollupWarnings,
            };
        }
    }
    return { rows: [], dailyExecutionSeries: [], rollupWarnings: [] };
};

/**
 * Normaliza um objeto de cliente impactado, mapeando qualquer propriedade para 'p'.
 * Isso resolve o problema de inconsistência onde a API pode retornar objetos com
 * diferentes nomes de propriedade (p, q, etc.) mas com a mesma estrutura.
 */
const toNumberOrZero = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const buildImpactedClient = (
    account: string,
    name: string,
    perfil: string,
    volumeOportunidade: number,
    extras: Pick<OpportunityImpactedClient, "advisor_name" | "advisor_id" | "advisor_element_id">,
    rest: Record<string, unknown> = {},
): OpportunityImpactedClient => ({
    account,
    name,
    volumeOportunidade,
    ...extras,
    p: { name, account, perfil },
    ...rest,
});

/**
 * Normaliza um objeto de cliente impactado para o contrato flat (+ `p` legado).
 */
const normalizeImpactedClient = (impactedClient: any): OpportunityImpactedClient => {
    if (!impactedClient || typeof impactedClient !== "object") {
        return buildImpactedClient("", "", "", 0, {}, {});
    }

    const volumeOportunidade = toNumberOrZero(
        impactedClient.volumeOportunidade ?? impactedClient.volume_oportunidade,
    );
    const extras = impactedAdvisorExtras(impactedClient as Record<string, unknown>);

    if (impactedClient.p && typeof impactedClient.p === "object") {
        const account = normalizeOpportunityAccountId(impactedClient.p.account ?? impactedClient.account);
        const name = impactedClient.p.name ?? impactedClient.name ?? "";
        const perfil = impactedClient.p.perfil ?? impactedClient.perfil ?? "";
        const { p: _p, volumeOportunidade: _v, volume_oportunidade: _vo, ...rest } = impactedClient;
        return buildImpactedClient(account, name, perfil, volumeOportunidade, extras, rest);
    }

    if (
        "p.name" in impactedClient ||
        "p.account" in impactedClient ||
        "p.perfil" in impactedClient
    ) {
        const account = normalizeOpportunityAccountId(impactedClient["p.account"] ?? impactedClient.account);
        const name = impactedClient["p.name"] ?? impactedClient.name ?? "";
        const perfil = impactedClient["p.perfil"] ?? impactedClient.perfil ?? "";
        return buildImpactedClient(account, name, perfil, volumeOportunidade, extras);
    }

    if ("name" in impactedClient || "account" in impactedClient) {
        const account = normalizeOpportunityAccountId(impactedClient.account);
        const name = impactedClient.name ?? "";
        const perfil = impactedClient.perfil ?? "";
        const { account: _a, name: _n, perfil: _pf, volumeOportunidade: _v, volume_oportunidade: _vo, advisor_id: _ai, advisor_name: _an, advisor_element_id: _ae, ...rest } =
            impactedClient;
        return buildImpactedClient(account, name, perfil, volumeOportunidade, extras, rest);
    }

    const keys = Object.keys(impactedClient);
    for (const key of keys) {
        const value = impactedClient[key];
        if (value && typeof value === "object" && ("name" in value || "account" in value || "perfil" in value)) {
            const account = normalizeOpportunityAccountId(value.account);
            const name = value.name ?? "";
            const perfil = value.perfil ?? "";
            return buildImpactedClient(account, name, perfil, volumeOportunidade, extras);
        }
    }

    return buildImpactedClient("", "", "", volumeOportunidade, extras);
};

/**
 * Normaliza a lista de clientes impactados de uma oportunidade.
 */
const normalizeImpactedClients = (impactedClients: any[]): OpportunityImpactedClient[] => {
    if (!Array.isArray(impactedClients)) {
        return [];
    }
    return impactedClients.map(normalizeImpactedClient);
};

/**
 * Interface para os parâmetros de filtro das oportunidades
 */
export interface IFetchOpportunitiesFilters {
    sort_direction?: 'ASC' | 'DESC';
    min_clients_impacted?: number;
    max_clients_impacted?: number;
    min_days?: number;
    max_days?: number;
    /** Heavy payload: portfolio movement rollups per objective. Omit or false for light list. */
    include_movement_rollups?: boolean;
    /** Include objectives past their endDate. */
    include_expired?: boolean;
}

/**
 * Faz a requisição para o endpoint de lista de oportunidades.
 *
 * Endpoint:
 *   http://localhost:8080/objectives/list_objectives
 */
export const fetchOpportunities = async (filters?: IFetchOpportunitiesFilters): Promise<IOpportunitiesResponse> => {
    const params = new URLSearchParams();
    
    if (filters) {
        if (filters.sort_direction) {
            params.append('sort_direction', filters.sort_direction);
        }
        if (filters.min_clients_impacted !== undefined) {
            params.append('min_clients_impacted', String(filters.min_clients_impacted));
        }
        if (filters.max_clients_impacted !== undefined) {
            params.append('max_clients_impacted', String(filters.max_clients_impacted));
        }
        if (filters.min_days !== undefined) {
            params.append('min_days', String(filters.min_days));
        }
        if (filters.max_days !== undefined) {
            params.append('max_days', String(filters.max_days));
        }
        if (filters.include_movement_rollups === true) {
            params.append('include_movement_rollups', 'true');
        }
        if (filters.include_expired === true) {
            params.append('include_expired', 'true');
        }
    }
    
    const endpoint = params.toString() 
        ? `/objectives/list_objectives?${params.toString()}`
        : "/objectives/list_objectives";
    
    const data = await apiFetch(endpoint);
    
    // Normaliza os impacted_clients de todas as oportunidades quando os dados são recebidos
    if (data && data.objectives && Array.isArray(data.objectives)) {
        data.objectives = data.objectives.map((objective: any) => {
            const rawRollups =
                objective.movement_portfolio_rollups ??
                objective.movementPortfolioRollups ??
                objective.portfolio_rollups;
            const { rows, summary: rollupSummary, dailyExecutionSeries, rollupWarnings } =
                normalizeMovementPortfolioRollupsField(rawRollups);

            const baseSummary =
                objective.summary ?? objective.movement_summary ?? undefined;
            const mergedSummary: ObjectiveMovementSummary | undefined =
                rollupSummary || baseSummary
                    ? {
                          ...(typeof baseSummary === "object" && baseSummary ? baseSummary : {}),
                          ...(rollupSummary && typeof rollupSummary === "object" ? rollupSummary : {}),
                          total_amount_net:
                              (rollupSummary as ObjectiveMovementSummary | undefined)
                                  ?.grand_total_amount_net ??
                              (rollupSummary as ObjectiveMovementSummary | undefined)?.total_amount_net ??
                              (baseSummary as ObjectiveMovementSummary | undefined)?.total_amount_net,
                      }
                    : undefined;

            const topWarnings = objective.warnings ?? objective.movement_warnings;
            const warnTop = Array.isArray(topWarnings)
                ? topWarnings.filter((w: unknown): w is string => typeof w === "string")
                : [];
            const warningsMerged = [...warnTop, ...rollupWarnings];

            const dueDateRaw = objective.dueDate ?? objective.due_date
            const isExpiredRaw = objective.isExpired ?? objective.is_expired

            const topDaily =
                objective.daily_execution_series ?? objective.dailyExecutionSeries
            const daily_execution_series =
                dailyExecutionSeries.length > 0
                    ? dailyExecutionSeries
                    : normalizeDailyExecutionSeries(topDaily)

            return {
                ...objective,
                dueDate: typeof dueDateRaw === "string" ? dueDateRaw : undefined,
                isExpired: typeof isExpiredRaw === "boolean" ? isExpiredRaw : undefined,
                impacted_clients: normalizeImpactedClients(objective.impacted_clients || []),
                has_movements_cypher:
                    objective.has_movements_cypher ?? objective.hasMovementsCypher,
                movement_portfolio_rollups: rows,
                daily_execution_series,
                summary: mergedSummary,
                warnings: warningsMerged.length ? warningsMerged : undefined,
            };
        });
    }
    
    return data as IOpportunitiesResponse;
};

/** Legacy direct create (lista). Prefer draft flow for gestores. */
export const createNewOpportunity = async (
    opportunityData: ObjectiveDraftCreateRequest | ObjectiveDraftCreateRequest[]
) => {
    const payload = Array.isArray(opportunityData) ? opportunityData : [opportunityData];
    const data = await apiFetch("/objectives/create_objective", {
        method: "POST",
        body: JSON.stringify(payload),
    });
    return data;
};

export const createObjectiveDraft = async (
    body: ObjectiveDraftCreateRequest
): Promise<ObjectiveDraftResponse> => {
    return apiFetch("/objectives/draft", {
        method: "POST",
        body: JSON.stringify(body),
    }) as Promise<ObjectiveDraftResponse>;
};

export const regenerateObjectiveDraft = async (
    body: ObjectiveDraftRegenerateRequest
): Promise<ObjectiveDraftResponse> => {
    return apiFetch("/objectives/draft/regenerate", {
        method: "POST",
        body: JSON.stringify(body),
    }) as Promise<ObjectiveDraftResponse>;
};

export const commitObjectiveDraft = async (
    body: ObjectiveDraftCommitRequest
): Promise<ObjectiveDraftCommitResponse> => {
    return apiFetch("/objectives/draft/commit", {
        method: "POST",
        body: JSON.stringify(body),
    }) as Promise<ObjectiveDraftCommitResponse>;
};

export const updateObjective = async (
    objectiveId: string,
    /**
     * `createdDate`/`endDate` em ISO (YYYY-MM-DD). A janela é a fonte da verdade:
     * o backend recalcula os vínculos da oportunidade ao alterá-la.
     */
    body: { title?: string; createdDate?: string; endDate?: string },
) => {
    const params = new URLSearchParams({ objective_id: objectiveId })
    const data = await apiFetch(`/objectives/update_objective?${params.toString()}`, {
        method: "PATCH",
        body: JSON.stringify(body),
    })
    return data
}

export const deleteObjective = async (objectiveId: string) => {
    const params = new URLSearchParams({
        objective_id: objectiveId,
    });
    const data = await apiFetch(`/objectives/delete_objective?${params.toString()}`, {
        method: "DELETE",
    });
    return data;
}


